/**
 * Clients' webhooks as staff run them (docs/39_WEBHOOKS.md): the platform's
 * settings, a client's switches and limits, and the delivery log.
 *
 * Every limit is the server's to enforce; a form here only says what it will
 * send. A field left blank means "follow the level above", and the server says
 * what that comes to (`limits` beside each `override`).
 */

import type { Tone } from '@connect/web-shared';

export const PLATFORM = '/messaging/client-webhooks';
export const webhookKeys = {
  all: ['webhooks'] as const,
  platform: ['webhooks', 'platform'] as const,
  usage: ['webhooks', 'usage'] as const,
  deliveries: ['webhooks', 'deliveries'] as const,
  delivery: (id: string) => ['webhooks', 'delivery', id] as const,
  client: (client: string) => ['webhooks', 'client', client] as const,
};

export interface LimitSet {
  max_endpoints: number;
  max_events_per_endpoint: number | null;
  max_per_minute: number;
}

export interface LimitOverride {
  max_endpoints: number | null;
  max_events_per_endpoint: number | null;
  max_per_minute: number | null;
}

export interface FamilyOverride extends LimitOverride {
  enabled: boolean | null;
}

export interface RetryPolicy {
  max_attempts: number;
  base_seconds: number;
  factor: number;
  max_backoff_seconds: number;
  timeout_seconds: number;
}

export interface PlatformWebhooks {
  enabled: boolean;
  max_endpoints_per_client: number;
  account: LimitSet;
  services: LimitSet;
  families: Record<string, Partial<FamilyOverride>>;
  retry: RetryPolicy;
  disable_after_failures: number;
  disable_after_hours: number;
  retention_days: number;
}

export interface StaffFamily {
  id: string;
  label: string;
  kind: 'account' | 'channel' | 'calls';
  offered: boolean;
  enabled_override: boolean | null;
  enabled: boolean;
  override: LimitOverride;
  limits: LimitSet;
  endpoints: number;
}

export interface StaffEndpoint {
  public_id: string;
  family: string;
  url: string;
  description: string | null;
  events: string[];
  status: 'active' | 'paused' | 'disabled';
  disabled_reason: 'client' | 'staff' | 'failing' | null;
  verified_at: string | null;
  last_delivered_at: string | null;
  last_failed_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
}

export interface ClientWebhooks {
  enabled: boolean;
  platform_enabled: boolean;
  max_endpoints_override: number | null;
  max_endpoints: number;
  families: StaffFamily[];
  webhooks: StaffEndpoint[];
  usage: Record<string, number>;
}

export interface StaffDelivery {
  public_id: string;
  event_id: string;
  event: string;
  family: string;
  status: 'pending' | 'sending' | 'delivered' | 'failed' | 'cancelled';
  attempts: number;
  redeliveries: number;
  response_status: number | null;
  last_error: string | null;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
  webhook_public_id: string;
  url: string;
  client_public_id: string | null;
  client_name: string | null;
}

export interface StaffDeliveryDetail extends StaffDelivery {
  payload: Record<string, unknown>;
  attempts_log: {
    number: number;
    at: string;
    duration_ms: number | null;
    response_status: number | null;
    response_body: string | null;
    error: string | null;
    succeeded: boolean;
  }[];
}

export interface UsageRow {
  client_public_id: string;
  client_name: string;
  endpoints: number;
  delivered: number;
  failed: number;
  pending: number;
  cancelled: number;
}

export const DELIVERY_TONES: Record<StaffDelivery['status'], Tone> = {
  pending: 'warning',
  sending: 'info',
  delivered: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

/** What an endpoint is doing, as staff read it. */
export function endpointState(endpoint: Pick<StaffEndpoint, 'status' | 'disabled_reason' | 'verified_at'>): {
  label: string;
  tone: Tone;
} {
  if (endpoint.status === 'disabled') {
    return endpoint.disabled_reason === 'failing'
      ? { label: 'Off: kept failing', tone: 'danger' }
      : { label: 'Off: switched off by staff', tone: 'neutral' };
  }
  if (endpoint.status === 'paused') return { label: 'Paused by the client', tone: 'neutral' };
  if (!endpoint.verified_at) return { label: 'Not verified', tone: 'warning' };
  return { label: 'Receiving', tone: 'success' };
}

/** A limit as a form holds it: blank follows the level above. */
export function limitText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** A form's limit back to what the API takes: blank is `null`, else a whole number. */
export function limitValue(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : Number.NaN;
}

/** A tri-state switch as a select: follow, on, off. */
export function switchText(value: boolean | null | undefined): '' | 'on' | 'off' {
  return value === true ? 'on' : value === false ? 'off' : '';
}

export function switchValue(text: string): boolean | null {
  return text === 'on' ? true : text === 'off' ? false : null;
}

export interface FamilyForm {
  enabled: '' | 'on' | 'off';
  max_endpoints: string;
  max_events_per_endpoint: string;
  max_per_minute: string;
}

export function familyForm(family: StaffFamily): FamilyForm {
  return {
    enabled: switchText(family.enabled_override),
    max_endpoints: limitText(family.override.max_endpoints),
    max_events_per_endpoint: limitText(family.override.max_events_per_endpoint),
    max_per_minute: limitText(family.override.max_per_minute),
  };
}

/**
 * The families the client's settings carry: only those with something set, so
 * one left alone keeps following the platform. `null` when a limit is not a
 * whole number — the form says so rather than sending it.
 */
export function familiesPayload(forms: Record<string, FamilyForm>): Record<string, Partial<FamilyOverride>> | null {
  const out: Record<string, Partial<FamilyOverride>> = {};
  for (const [id, form] of Object.entries(forms)) {
    const entry: Partial<FamilyOverride> = {};
    const enabled = switchValue(form.enabled);
    if (enabled !== null) entry.enabled = enabled;
    for (const key of ['max_endpoints', 'max_events_per_endpoint', 'max_per_minute'] as const) {
      const value = limitValue(form[key]);
      if (Number.isNaN(value)) return null;
      if (value !== null) entry[key] = value;
    }
    if (Object.keys(entry).length) out[id] = entry;
  }
  return out;
}
